/* sugar:assets/announcement-bar.js@2.0.0 */
/**
 * Announcement bar — the slider and the sticky behaviour behind sections/announcement-bar.liquid.
 *
 * One copy of this file serves every announcement bar on the page. The markup carries
 * data attributes only, so nothing here depends on a class name:
 *
 *   [data-ab-root]          the bar itself, carrying its whole config
 *     data-ab-sticky        none | always | on-scroll-up
 *   [data-ab-splide]        the track, present only when the slider is on for some viewport
 *     data-ab-slider-on     mobile | desktop | both
 *     data-ab-type          snap | fade
 *     data-ab-autoplay      true | false
 *     data-ab-autoplay-speed  seconds
 *
 * The splide scaffold is rendered server-side and simply left unmounted on a viewport
 * set to grid, so nothing is built or torn down by DOM surgery on resize, and a slow
 * vendor chunk never blanks the bar. A bar with the slider off renders no `.splide`
 * markup at all, so the Splide vendor bundle is never fetched for it.
 */

(function () {
  'use strict';

  if (window.SugarAnnouncementBar) return;

  var RETRY_MS = 120;
  var MOBILE = '(max-width: 749px)';

  function syncTopChrome() {
    if (typeof window.sugarThemeSyncTopChrome === 'function') window.sugarThemeSyncTopChrome();
  }

  /* ------------------------------------------------------------------ */
  /* Slider                                                             */
  /* ------------------------------------------------------------------ */

  /**
   * Splide leaves inline sizes behind when a slider is torn down. Clear them so the
   * same markup lays out as a plain flex row again.
   */
  function clearSplideStyles(el) {
    el.classList.remove('is-initialized', 'is-active', 'is-overflow', 'is-rendered');
    el.style.visibility = '';

    var track = el.querySelector('.splide__track');
    if (track) {
      track.style.transform = '';
      track.style.paddingLeft = '';
      track.style.paddingRight = '';
      track.style.height = '';
    }

    var list = el.querySelector('.splide__list');
    if (!list) return;
    list.style.transform = '';
    list.style.transition = '';
    list.style.width = '';
    list.style.height = '';

    Array.prototype.forEach.call(list.children, function (child) {
      if (!child || !child.style) return;
      child.style.width = '';
      child.style.marginRight = '';
      child.style.marginLeft = '';
      child.style.transform = '';
    });
  }

  /**
   * Splide takes the track list's direct children as its slides, and Shopify wraps every
   * theme block in a div of its own — so the wrapper is the slide, not the column inside it.
   */
  function prepareSlides(el) {
    var list = el.querySelector('.splide__list');
    if (!list) return;
    Array.prototype.forEach.call(list.children, function (child) {
      child.classList.add('splide__slide');
    });
  }

  function destroy(el) {
    if (el._scChrome) {
      try {
        el._scChrome.detach();
      } catch (e) {}
      el._scChrome = null;
    }
    if (el._splideInstance) {
      try {
        el._splideInstance.destroy(true);
      } catch (e) {}
      el._splideInstance = null;
    }
  }

  /** Returns false when Splide was wanted but is not available yet, so the caller retries. */
  function mountSlider(el) {
    var root = el.closest('[data-ab-root]');

    function setGridMode(on) {
      el.toggleAttribute('data-ab-grid-mode', on);
    }

    destroy(el);

    var onMobile = window.matchMedia(MOBILE).matches;
    var on = el.dataset.abSliderOn;
    var wanted = on === 'both' || (onMobile ? on === 'mobile' : on === 'desktop');

    if (!wanted) {
      clearSplideStyles(el);
      setGridMode(true);
      return true;
    }

    if (typeof Splide === 'undefined') {
      clearSplideStyles(el);
      setGridMode(true);
      return false;
    }

    setGridMode(false);
    prepareSlides(el);

    var isFade = el.dataset.abType === 'fade';
    var autoplay = el.dataset.abAutoplay === 'true';
    var speed = parseFloat(el.dataset.abAutoplaySpeed);
    if (isNaN(speed) || speed <= 0) speed = 5;

    var instance;
    try {
      instance = new Splide(el, {
        type: isFade ? 'fade' : 'loop',
        rewind: isFade,
        perPage: 1,
        perMove: 1,
        arrows: false,
        pagination: false,
        autoplay: autoplay,
        interval: speed * 1000,
        // Announcements are read, not skimmed: never slide one away from under the
        // pointer or the keyboard focus. Deliberately not a merchant setting.
        pauseOnHover: true,
        pauseOnFocus: true,
        speed: isFade ? 600 : 400,
        easing: 'ease',
        gap: 0
      });
    } catch (e) {
      clearSplideStyles(el);
      setGridMode(true);
      return false;
    }

    el._splideInstance = instance;
    instance.mount();

    if (window.SugarSliderChrome && root) {
      el._scChrome = window.SugarSliderChrome.attach(instance, root, { dotMode: 'slide' });
    }

    // A bar that changes height between slides would shove the page down mid-read.
    instance.on('mounted resized', syncTopChrome);

    return true;
  }

  /* ------------------------------------------------------------------ */
  /* Sticky                                                             */
  /* ------------------------------------------------------------------ */

  /**
   * `on-scroll-up`: the bar scrolls away with the page, then pins itself back to the top
   * as soon as the visitor scrolls up. `always` needs no script — it is pure CSS.
   *
   * The pinning goes on Shopify's own section wrapper rather than on the bar: that wrapper
   * is the element sitting in the page's flow, and `position: sticky` on anything inside it
   * would only stick within a box that is already exactly the bar's height.
   */
  function StickyBar(bar) {
    var root = bar.closest('.shopify-section') || bar.parentElement;
    var lastScroll = window.pageYOffset || document.documentElement.scrollTop;
    var top = 0;
    var height = 0;
    var ticking = false;

    function measure() {
      var offset = 0;
      var node = root;
      while (node) {
        offset += node.offsetTop || 0;
        node = node.offsetParent;
      }
      top = offset;
      height = Math.round(root.offsetHeight || 0);
      root.style.setProperty('--ab-height', height + 'px');
    }

    function setState(next) {
      if (root.getAttribute('data-ab-pinned') === next) return;
      if (next) root.setAttribute('data-ab-pinned', next);
      else root.removeAttribute('data-ab-pinned');
      syncTopChrome();
      requestAnimationFrame(syncTopChrome);
    }

    function evaluate() {
      var scroll = window.pageYOffset || document.documentElement.scrollTop;
      var delta = scroll - lastScroll;

      if (scroll <= top + height) {
        setState('');
      } else if (Math.abs(delta) < 6) {
        lastScroll = scroll;
        return;
      } else if (delta > 0) {
        setState('hidden');
      } else {
        setState('shown');
      }

      lastScroll = scroll;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        evaluate();
        ticking = false;
      });
    }

    function onResize() {
      measure();
      evaluate();
    }

    this.refresh = function () {
      measure();
      lastScroll = window.pageYOffset || document.documentElement.scrollTop;
      evaluate();
    };

    this.refresh();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    this.onResize = onResize;
  }

  var stickyBars = [];

  function initSticky(scope) {
    var host = scope && scope.querySelectorAll ? scope : document;
    var bars = host.querySelectorAll('[data-ab-root][data-ab-sticky="on-scroll-up"]');
    Array.prototype.forEach.call(bars, function (bar) {
      if (bar._abSticky) {
        bar._abSticky.refresh();
        return;
      }
      bar._abSticky = new StickyBar(bar);
      stickyBars.push(bar._abSticky);
    });
  }

  /* ------------------------------------------------------------------ */

  var pending = false;

  function init(scope) {
    var host = scope && scope.querySelectorAll ? scope : document;
    var ready = true;

    Array.prototype.forEach.call(host.querySelectorAll('[data-ab-splide]'), function (el) {
      if (!mountSlider(el)) ready = false;
    });

    initSticky(scope);
    syncTopChrome();

    if (!ready && !pending) {
      pending = true;
      setTimeout(function () {
        pending = false;
        init(scope);
      }, RETRY_MS);
    }
  }

  window.SugarAnnouncementBar = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // The viewport can cross the breakpoint between grid and slider.
  var mq = window.matchMedia(MOBILE);
  var onChange = function () {
    init(document);
  };
  if (mq.addEventListener) mq.addEventListener('change', onChange);
  else if (mq.addListener) mq.addListener(onChange);

  /* A resize that does not cross the breakpoint leaves the slider mounted, and Splide
     holds the widths it measured when it mounted — so a phone rotating, or a scrollbar
     appearing, would otherwise leave the track offset by the difference. */
  var resizeTimer = null;
  window.addEventListener('resize', function () {
    stickyBars.forEach(function (bar) {
      bar.onResize();
    });
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      document.querySelectorAll('[data-ab-splide]').forEach(function (el) {
        if (!el._splideInstance) return;
        try {
          el._splideInstance.refresh();
        } catch (e) {}
      });
    }, 150);
  }, { passive: true });

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      init(event.target);
    });
    document.addEventListener('shopify:section:unload', syncTopChrome);
    document.addEventListener('shopify:section:reorder', syncTopChrome);

    // Bring the selected column into view, and hold autoplay while it is being edited.
    document.addEventListener('shopify:block:select', function (event) {
      var el = event.target.closest('[data-ab-splide]');
      if (!el || !el._splideInstance) return;
      var list = el.querySelector('.splide__list');
      var slides = list ? Array.prototype.slice.call(list.children) : [];
      var index = slides.findIndex(function (slide) {
        return slide === event.target || slide.contains(event.target);
      });
      if (index !== -1) el._splideInstance.go(index);
      var autoplay = el._splideInstance.Components.Autoplay;
      if (autoplay) autoplay.pause();
    });

    document.addEventListener('shopify:block:deselect', function (event) {
      var el = event.target.closest('[data-ab-splide]');
      if (!el || !el._splideInstance) return;
      var autoplay = el._splideInstance.Components.Autoplay;
      if (autoplay) autoplay.play();
    });
  }
})();
